import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "@phosphor-icons/react";

const FERROUS =
  "https://images.unsplash.com/photo-1697698532634-ea59b636ccea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwc3RlZWwlMjByb2xsc3xlbnwwfHx8fDE3Nzg4MTY2NDl8MA&ixlib=rb-4.1.0&q=85";
const NONFERROUS =
  "https://static.prod-images.emergentagent.com/jobs/f7842974-09b3-4b3e-bebd-5a0c602980d0/images/782c9836b21abeb0e3e0f4bb2d486bb579f4ab120bca9833889e6ab12c1c98b0.png";
const SCRAP =
  "https://static.prod-images.emergentagent.com/jobs/f7842974-09b3-4b3e-bebd-5a0c602980d0/images/ffaf51ae228762292aeab5986574827f05a656b3d1cc1a60a3a50c335efe5309.png";

const commodities = [
  {
    img: FERROUS,
    code: "01",
    title: "Ferrous Metals",
    items: ["HRC", "CRC", "GI / PPGI", "HR Plates", "Pipes", "Structural Steel"],
    desc:
      "Flat and long ferrous products sourced from established mills, traded on disciplined commercial terms.",
  },
  {
    img: NONFERROUS,
    code: "02",
    title: "Non-Ferrous Metals",
    items: ["Copper", "Aluminium", "Zinc", "Lead", "Nickel"],
    desc:
      "Refined non-ferrous metals supplied to industrial buyers, with quality assurance and reliable delivery.",
  },
  {
    img: SCRAP,
    code: "03",
    title: "Metal Scrap & Raw Materials",
    items: ["Ferrous scrap", "Non-ferrous scrap", "Industrial raw materials"],
    desc:
      "Cleanly processed scrap and feedstock — traded across selected corridors for re-rolling and refining.",
  },
];

export const Commodities = () => {
  return (
    <section
      id="commodities"
      className="relative bg-vynex-navy-deep text-white py-28 lg:py-36 overflow-hidden"
      data-testid="commodities-section"
    >
      <div className="absolute inset-0 grain" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 mb-16 lg:mb-20">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-vynex-gold" />
              <span className="eyebrow text-vynex-gold">Commodities</span>
            </div>
            <h2 className="font-display text-4xl lg:text-[56px] leading-[1.05] tracking-tight">
              Focused coverage across{" "}
              <span className="italic text-vynex-silver">metals markets.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 text-vynex-silver/70 leading-relaxed self-end">
            From mill to port to industrial buyer — we operate within the
            ferrous and non-ferrous value chain with discipline and commercial
            discretion.
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {commodities.map((c, i) => (
            <motion.article
              key={c.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="group relative bg-white/[0.03] border border-white/10 overflow-hidden flex flex-col"
              data-testid={`commodity-card-${i}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={c.img}
                  alt={c.title}
                  className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-vynex-navy-deep via-vynex-navy-deep/40 to-transparent" />
                <div className="absolute top-5 left-5 eyebrow text-vynex-gold">
                  {c.code}
                </div>
                <ArrowUpRight
                  size={22}
                  weight="thin"
                  className="absolute top-5 right-5 text-white/70 group-hover:text-vynex-gold transition-colors"
                />
              </div>
              <div className="p-7 lg:p-9 flex-1 flex flex-col">
                <h3 className="font-display text-2xl lg:text-3xl text-white">
                  {c.title}
                </h3>
                <p className="mt-4 text-vynex-silver/70 text-sm leading-relaxed">
                  {c.desc}
                </p>
                <ul className="mt-6 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-white/10 pt-5">
                  {c.items.map((it) => (
                    <li
                      key={it}
                      className="text-xs text-vynex-silver/60 tracking-wide flex items-center gap-2"
                    >
                      <span className="w-1 h-1 bg-vynex-gold" />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Commodities;
